import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { userOperatorBlocksAPI, userAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { ShieldOff, Plus, Pencil, Trash2, Loader2, Save } from 'lucide-react';

const OPERATORS = ['jio', 'airtel', 'vi', 'bsnl_topup', 'bsnl_special', 'jio_lite', 'mtnl', 'tatasky', 'dishtv', 'd2h', 'airtel_dth', 'sun_direct'];
const ROLES = [{ value: 'retailer', label: 'All Retailers' }, { value: 'distributor', label: 'All Distributors' }, { value: 'super_distributor', label: 'All Super Distributors' }];

const UserOperatorBlocks = () => {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ block_type: 'role', user_id: '', role: 'retailer', operator: '', reason: '', status: 'active' });

  useEffect(() => { fetchAll(); }, []);
  const fetchAll = async () => {
    try {
      const [r, u] = await Promise.all([userOperatorBlocksAPI.list(), userAPI.getUsers()]);
      setItems(r.data || []);
      setUsers((u.data || []).filter(u => u.role !== 'admin'));
    } catch {} finally { setLoading(false); }
  };
  const fetchItems = async () => { try { const r = await userOperatorBlocksAPI.list(); setItems(r.data || []); } catch {} };
  const reset = () => { setForm({ block_type: 'role', user_id: '', role: 'retailer', operator: '', reason: '', status: 'active' }); setEditId(null); };
  const openEdit = (i) => {
    setForm({
      block_type: i.block_type || 'role', user_id: i.user_id || '', role: i.role || 'retailer',
      operator: i.operator, reason: i.reason || '', status: i.status
    });
    setEditId(i.id); setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.operator) { toast.error('Operator is required'); return; }
    if (form.block_type === 'user' && !form.user_id) { toast.error('Please select a user'); return; }
    if (form.block_type === 'role' && !form.role) { toast.error('Please select a role'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.block_type === 'role') payload.user_id = '';
      if (form.block_type === 'user') payload.role = '';
      if (editId) await userOperatorBlocksAPI.update(editId, payload); else await userOperatorBlocksAPI.create(payload);
      toast.success(editId ? 'Updated' : 'Block created'); fetchItems(); setDialogOpen(false); reset();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  const handleDelete = async (id) => { if (!window.confirm('Remove this block?')) return; try { await userOperatorBlocksAPI.delete(id); toast.success('Removed'); fetchItems(); } catch { toast.error('Failed'); } };
  const handleToggle = async (i) => { try { await userOperatorBlocksAPI.update(i.id, { status: i.status === 'active' ? 'inactive' : 'active' }); fetchItems(); } catch {} };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="user-operator-blocks-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldOff className="w-6 h-6" style={{ color: '#4680ff' }} />User Operator Block</h1>
            <p className="text-muted-foreground">Block specific operators for users or roles</p>
          </div>
          <Button onClick={() => { reset(); setDialogOpen(true); }} className="bg-[#4680ff] hover:bg-[#2563eb] text-white shadow-lg shadow-blue-500/20 rounded-lg h-10 px-5 text-sm font-semibold btn-press" data-testid="add-block-btn"><Plus className="w-4 h-4 mr-2" />Add Block</Button>
        </div>

        {items.length > 0 && <Card><CardContent className="p-4">
          <div className="flex items-center gap-8">
            <div><p className="text-sm text-muted-foreground">Total Blocks</p><p className="text-2xl font-bold">{items.length}</p></div>
            <div><p className="text-sm text-muted-foreground">Active</p><p className="text-2xl font-bold text-red-600">{items.filter(i => i.status === 'active').length}</p></div>
            <div><p className="text-sm text-muted-foreground">By Role</p><p className="text-2xl font-bold" style={{ color: '#4680ff' }}>{items.filter(i => i.block_type === 'role').length}</p></div>
            <div><p className="text-sm text-muted-foreground">By User</p><p className="text-2xl font-bold text-orange-600">{items.filter(i => i.block_type === 'user').length}</p></div>
          </div>
        </CardContent></Card>}

        <Card><CardContent className="p-0">
          {loading ? <div className="p-6 space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
            : items.length === 0 ? <div className="text-center py-12 text-muted-foreground"><ShieldOff className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No operator blocks configured</p><p className="text-sm mt-1">Block operators for specific users or entire roles</p></div>
              : <div className="overflow-x-auto"><Table><TableHeader><TableRow><TableHead>Block Type</TableHead><TableHead>User / Role</TableHead><TableHead>Operator</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>{items.map(i => (
                  <TableRow key={i.id}>
                    <TableCell><Badge variant={i.block_type === 'role' ? 'default' : 'outline'} className="capitalize">{i.block_type}</Badge></TableCell>
                    <TableCell className="font-medium">{i.user_name || '-'}</TableCell>
                    <TableCell className="capitalize font-semibold">{i.operator?.replace('_', ' ')}</TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">{i.reason || '-'}</TableCell>
                    <TableCell><button onClick={() => handleToggle(i)}><Badge className={i.status === 'active' ? 'bg-red-100 text-red-800 cursor-pointer' : 'bg-green-100 text-green-800 cursor-pointer'}>{i.status === 'active' ? 'blocked' : 'allowed'}</Badge></button></TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(i)}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(i.id)}><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}</TableBody></Table></div>}
        </CardContent></Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}><DialogContent data-testid="block-dialog"><DialogHeader><DialogTitle>{editId ? 'Edit' : 'Add'} Operator Block</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Block Type</Label>
              <Select value={form.block_type} onValueChange={v => setForm({ ...form, block_type: v, user_id: '', role: v === 'role' ? 'retailer' : '' })}><SelectTrigger data-testid="block-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="role">By Role (all users of a role)</SelectItem><SelectItem value="user">By User (specific user)</SelectItem></SelectContent></Select>
            </div>
            {form.block_type === 'role' ? (
              <div className="space-y-2"><Label>Role *</Label>
                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}><SelectTrigger data-testid="block-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select>
              </div>
            ) : (
              <div className="space-y-2"><Label>User *</Label>
                <Select value={form.user_id} onValueChange={v => setForm({ ...form, user_id: v })}><SelectTrigger data-testid="block-user"><SelectValue placeholder="Select user" /></SelectTrigger>
                  <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role}) - {u.email}</SelectItem>)}</SelectContent></Select>
              </div>
            )}
            <div className="space-y-2"><Label>Operator *</Label>
              <Select value={form.operator} onValueChange={v => setForm({ ...form, operator: v })}><SelectTrigger data-testid="block-operator"><SelectValue placeholder="Select operator" /></SelectTrigger>
                <SelectContent>{OPERATORS.map(o => <SelectItem key={o} value={o} className="capitalize">{o.replace('_', ' ')}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-2"><Label>Reason (optional)</Label><Input placeholder="Why is this operator blocked?" value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} data-testid="block-reason" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button onClick={handleSave} disabled={saving} className="bg-[#4680ff] hover:bg-[#2563eb] text-white shadow-lg shadow-blue-500/20 rounded-lg h-10 px-5 text-sm font-semibold btn-press" data-testid="save-block-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{editId ? 'Update' : 'Block'}</Button></DialogFooter>
        </DialogContent></Dialog>
      </div>
    </DashboardLayout>
  );
};
export default UserOperatorBlocks;
